import React, { ReactNode } from 'react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: ReactNode;
  maxItems?: number;
  className?: string;
  noTrailingSlash?: boolean;
  size?: 'sm' | 'md';
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  separator = '/',
  maxItems,
  className = '',
  noTrailingSlash = false,
  size = 'md'
}) => {
  // If maxItems is set and we have more items, collapse middle items
  let displayItems = items;
  let showEllipsis = false;
  
  if (maxItems && items.length > maxItems) {
    // When maxItems is 2 or less, show "... / [last item]"
    if (maxItems <= 2) {
      displayItems = [{ label: '...' }, items[items.length - 1]];
    } else {
      // Otherwise, show first items, ellipsis, and last items
      const firstCount = Math.floor((maxItems - 1) / 2);
      const lastCount = Math.ceil((maxItems - 1) / 2);
      const firstItems = items.slice(0, firstCount);
      const lastItems = items.slice(items.length - lastCount);
      displayItems = [...firstItems, { label: '...' }, ...lastItems];
    }
    showEllipsis = true;
  }
  
  const breadcrumbClasses = [
    'ui-breadcrumb',
    noTrailingSlash && 'ui-breadcrumb--no-trailing-slash',
    size === 'sm' && 'ui-breadcrumb--sm',
    className
  ].filter(Boolean).join(' ');
  
  return (
    <nav className={breadcrumbClasses} aria-label="Breadcrumb">
      <ol className="ui-breadcrumb-list">
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;
          const isEllipsis = showEllipsis && item.label === '...';
          
          return (
            <li key={index} className="ui-breadcrumb-item">
              {item.href && !isLast && !isEllipsis ? (
                <a
                  href={item.href}
                  className="ui-breadcrumb-link"
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  }}
                >
                  {item.icon && (
                    <span className="ui-breadcrumb-icon">{item.icon}</span>
                  )}
                  <span className="ui-breadcrumb-text">{item.label}</span>
                </a>
              ) : item.onClick && !isLast && !isEllipsis ? (
                <button
                  type="button"
                  className="ui-breadcrumb-link ui-breadcrumb-link--button"
                  onClick={item.onClick}
                >
                  {item.icon && (
                    <span className="ui-breadcrumb-icon">{item.icon}</span>
                  )}
                  <span className="ui-breadcrumb-text">{item.label}</span>
                </button>
              ) : (
                <span 
                  className={`ui-breadcrumb-text ${
                    isLast ? 'ui-breadcrumb-text--current' : ''
                  } ${isEllipsis ? 'ui-breadcrumb-text--ellipsis' : ''}`}
                >
                  {item.icon && !isEllipsis && (
                    <span className="ui-breadcrumb-icon">{item.icon}</span>
                  )}
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
